﻿import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, ArrowLeft, ArrowRight, Sparkles, Search, BookOpen, FileText, X, CheckCircle2, MousePointerClick } from "lucide-react";
import { Button } from "@foundation/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Badge } from "@foundation/components/ui/badge";
import { Input } from "@foundation/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@foundation/components/ui/alert-dialog";
import { useUser } from "@foundation/contextapi/UserContext";
import { useToast } from "@foundation/hooks/use-toast";
import BrandingService from "@foundation/services/brandingService";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";
import RevisrLayout from "../components/RevisrLayout";
import { PolicyStepBar } from "../components/boost-policy/BoostPolicyStepper";
import { LibrarySelectionStep } from "../components/boost-policy/LibrarySelectionStep";
import { PolicySetupStep } from "../components/boost-policy/PolicySetupStep";
import { PolicyContextQAStep } from "../components/boost-policy/PolicyContextQAStep";
import { GeneratePolicyStep } from "../components/boost-policy/GeneratePolicyStep";
import type {
  AiSkill,
  BoostPolicyBrandingData,
  BoostPolicyFormData,
  BoostPolicyKnowledgeDocument,
  BoostPolicyStep,
  BoostPolicyStepName,
  OrganizationProfileData,
} from "../types/boostPolicy";
import { STEP_NAME_TO_INDEX, STEP_INDEX_TO_NAME } from "../types/boostPolicy";
import {
  createBoostPolicyBrandPayload,
  createBoostPolicyDraft,
  getBoostPolicyById,
  getTemplateMetadataById,
  loadBoostPolicyKnowledgeDocuments,
  loadSelectedTemplate,
  updateBoostPolicyStep,
} from "../services/boostPolicyService";
import { getOrgContext } from "../services/organizationContextService";
import { fetchContextCategories, isCategoryUnlocked } from "../services/contextCategoryService";
import type { ContextCategory, StandardAnswers } from "../services/contextCategoryService";
import { listAiSkills } from "@foundation/services/aiSkillsService";
import { useSubscriptionUsage } from "../hooks/useSubscriptionUsage";

const STEPS: BoostPolicyStep[] = [
  {
    id: "policy-library",
    title: "Choose from Revisr Library",
    description:
      "Revisr Library is loaded with policy best practices aligned to industries, domains, and regions. Select a source document to align your policy output.",
  },
  {
    id: "policy-setup",
    title: "Policy setup",
    description: "Name your policy and review your organisation profile and branding.",
  },
  {
    id: "context-qa",
    title: "AI Context",
    description: "Answer a few questions to help the AI generate a more accurate, tailored policy.",
  },
  {
    id: "review",
    title: "Review & Boost",
    description: "Review all details and boost your policy.",
  },
];

const INITIAL_FORM_DATA: BoostPolicyFormData = {
  policyName: "",
  selectedKnowledgeDocument: null,
  organizationContext: "",
  industryTags: [],
  regionTags: [],
  workforceSize: "",
  selectedSkillIds: [],
  selectedTemplateId: null,
  selectedTemplateName: null,
  selectedTemplateData: null,
  relevantContext: [],
  additionalContext: '',
};

const LOREM_IPSUM_PATTERN = /^lorem ipsum dolor sit amet/i;

// —— Library Card Grid (Step 0) ———————————————————————

const PAGE_SIZE = 12;

type PolicyLibraryGridProps = {
  documents: BoostPolicyKnowledgeDocument[];
  isLoading: boolean;
  selectedDocId: string | null;
  onSelect: (doc: BoostPolicyKnowledgeDocument | null) => void;
  onContinue: () => void;
  isSaving: boolean;
  unlockedCategoryIds?: string[];
  categoryIdToName?: Record<string, string>;
};

const PolicyLibraryGrid = ({
  documents,
  isLoading,
  selectedDocId,
  onSelect,
  onContinue,
  isSaving,
  unlockedCategoryIds,
  categoryIdToName,
}: PolicyLibraryGridProps) => {
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Reset page on search change
  useEffect(() => { setCurrentPage(1); }, [query]);

  const filteredDocuments = useMemo(() => {
    const searchTerm = query.trim().toLowerCase();
    const filtered = documents.filter((doc) => {
      const matchesSearch =
        !searchTerm ||
        doc.title.toLowerCase().includes(searchTerm) ||
        doc.description.toLowerCase().includes(searchTerm) ||
        doc.tags.some((t) => t.toLowerCase().includes(searchTerm));
      return matchesSearch;
    });

    return filtered.sort((a, b) => {
      const catA = a.categoryMapping?.categoryId;
      const catB = b.categoryMapping?.categoryId;
      const lockedA = !!catA && !!unlockedCategoryIds && !unlockedCategoryIds.includes(catA);
      const lockedB = !!catB && !!unlockedCategoryIds && !unlockedCategoryIds.includes(catB);
      if (lockedA !== lockedB) return lockedA ? 1 : -1;
      if (lockedA && lockedB) {
        const nameA = (catA && categoryIdToName?.[catA]) ?? '';
        const nameB = (catB && categoryIdToName?.[catB]) ?? '';
        return nameA.localeCompare(nameB);
      }
      return 0;
    });
  }, [query, documents, unlockedCategoryIds, categoryIdToName]);

  const totalPages = Math.ceil(filteredDocuments.length / PAGE_SIZE);
  const pagedDocuments = filteredDocuments.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const selectedDoc = documents.find((d) => d.id === selectedDocId) ?? null;

  if (isLoading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-[#33b864]" />
        <p className="text-sm text-muted-foreground">Loading Revisr Library...</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-8 pt-8 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#33b864]/10 border border-[#33b864]/20 text-[#33b864] text-xs font-medium mb-4">
          <BookOpen className="h-3.5 w-3.5" />
          Step 1 of 4
        </div>
        <h1 className="text-2xl font-bold text-foreground leading-snug">
          Choose a policy document
        </h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl leading-relaxed">
          Select a policy from the Revisr Library. This will be used as the reference to generate an organisation-ready document tailored to your context.
        </p>
      </div>

      {/* Search + Filter */}
      <div className="px-8 pb-2 space-y-3">
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              placeholder="Search by title, description, tags or regions"
              className="pl-9 h-10 bg-white border-border rounded-lg shadow-sm"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-muted hover:bg-border flex items-center justify-center transition-colors"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
          </div>

        </div>
      </div>

      <div className="mx-8 mt-4 border-t border-border" />

      {/* Cards grid */}
      <div className="px-8 py-6">
        {filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="h-12 w-12 rounded-2xl bg-muted border border-border flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-muted-foreground/40" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">No results match your filters</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Try different search terms or remove filters.</p>
              <button
                onClick={() => setQuery("")}
                className="mt-3 text-xs text-[#33b864] hover:opacity-80 font-medium underline underline-offset-2 transition-colors"
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {pagedDocuments.map((doc) => {
                const catId = doc.categoryMapping?.categoryId;
                const isLocked = !!catId && !!unlockedCategoryIds && !unlockedCategoryIds.includes(catId);
                const lockCategoryName = isLocked && catId ? (categoryIdToName?.[catId]) : undefined;
                return (
                  <LibraryDocCard
                    key={doc.id}
                    doc={doc}
                    isSelected={selectedDocId === doc.id}
                    onSelect={onSelect}
                    isLocked={isLocked}
                    lockCategoryName={lockCategoryName}
                  />
                );
              })}
            </div>

            {/* Cross-page selected banner */}
            {selectedDoc && !pagedDocuments.some((d) => d.id === selectedDocId) && (
              <div className="mt-5 flex items-center gap-3 px-4 py-3 rounded-xl border border-[#33b864]/40 bg-[#33b864]/5 text-sm">
                <CheckCircle2 className="h-4 w-4 text-[#33b864] flex-shrink-0" />
                <span className="text-foreground flex-1 min-w-0">
                  <span className="font-medium text-[#33b864]">Selected on another page:</span>{" "}
                  <span className="truncate">{selectedDoc.title}</span>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const idx = filteredDocuments.findIndex((d) => d.id === selectedDocId);
                    if (idx !== -1) setCurrentPage(Math.ceil((idx + 1) / PAGE_SIZE));
                  }}
                  className="text-xs text-[#33b864] font-medium hover:opacity-80 underline underline-offset-2 flex-shrink-0"
                >
                  Jump to page
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={[
                      "w-9 h-9 rounded-lg text-sm font-medium border transition-colors",
                      currentPage === page
                        ? "bg-[#33b864] text-white border-[#33b864]"
                        : "border-border text-muted-foreground hover:bg-muted/50",
                    ].join(" ")}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium border border-border text-muted-foreground hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Continue footer */}
      <div className="border-t border-border px-8 py-4 bg-muted/30 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 min-w-0">
          {selectedDoc ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-[#33b864] flex-shrink-0" />
              <p className="text-sm text-foreground truncate">
                <span className="font-medium">Selected:</span> {selectedDoc.title}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Select a document to continue</p>
          )}
        </div>
        <Button
          onClick={onContinue}
          disabled={!selectedDoc || isSaving}
          className="gap-2 px-6 flex-shrink-0 bg-[#33b864] hover:bg-[#2da358] text-white border-0 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <><Loader2 className="h-4 w-4 animate-spin" />Savingâ€¦</>
          ) : (
            <>Continue<ArrowRight className="h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
};

// —— Individual selectable library card ———————————————————

type LibraryDocCardProps = {
  doc: BoostPolicyKnowledgeDocument;
  isSelected: boolean;
  onSelect: (doc: BoostPolicyKnowledgeDocument | null) => void;
  isLocked: boolean;
  lockCategoryName?: string;
};

const LibraryDocCard = ({ doc, isSelected, onSelect, isLocked, lockCategoryName }: LibraryDocCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const visibleTags = showAll ? doc.tags : doc.tags.slice(0, 3);

  const lockTitle = lockCategoryName
    ? `Complete the ${lockCategoryName} questionnaire to unlock`
    : 'Complete the questionnaire to unlock this document';

  return (
    <div
      className={[
        "group flex flex-col bg-white rounded-xl border shadow-sm transition-all duration-200 overflow-hidden",
        isLocked
          ? "opacity-50 pointer-events-none cursor-not-allowed border-border"
          : isSelected
            ? "hover:shadow-md border-[#33b864] ring-2 ring-[#33b864]/20"
            : "hover:shadow-md border-border hover:border-[#33b864]/30",
      ].join(" ")}
      title={isLocked ? lockTitle : undefined}
    >
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2 flex-1 pt-0.5">
            {doc.title}
          </h3>
        </div>

        {doc.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleTags.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="text-[10px] px-2 py-0.5 rounded-full font-bold text-[#33b864] border border-[#33b864]/40 bg-transparent"
              >
                {tag}
              </Badge>
            ))}
            {!showAll && doc.tags.length > 3 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                className="text-[10px] px-2 py-0.5 rounded-full font-normal text-primary border border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                +{doc.tags.length - 3}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-border px-5 py-3">
        {isLocked ? (
          <div className="flex items-center justify-center gap-1.5 h-8">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            <Badge variant="outline" className="text-[10px] px-2 py-0.5 font-semibold text-muted-foreground border-muted-foreground/30">
              Pro
            </Badge>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onSelect(isSelected ? null : doc)}
            className={[
              "w-full flex items-center justify-center gap-1.5 text-xs h-8 font-medium rounded-lg transition-all duration-200",
              isSelected
                ? "bg-[#33b864] text-white"
                : "bg-[#f97316] text-white hover:bg-[#ea580c]",
            ].join(" ")}
          >
            {isSelected ? (
              <><CheckCircle2 className="h-3.5 w-3.5" />Selected</>
            ) : (
              <><MousePointerClick className="h-3.5 w-3.5" />Select</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// —— Main Page ————————————————————————————————

type BoostPolicyFlowProps = {
  baseRoute?: string;
  isOnboarding?: boolean;
};

/**
 * Extract the document classification from the Q&A session pairs. The Boost
 * Policy Q&A skill asks this as its Tier 0 "always ask" first question
 * (migration 033). We scan every answer case-insensitively for one of the four
 * labels — "internal" alone normalises to "Internal Use". If no answer
 * matches (user skipped / gave no valid value) we default to "Public".
 */
const extractClassification = (pairs: { question?: string; answer?: string }[]): string => {
  for (const pair of pairs) {
    const ans = (pair?.answer ?? '').toLowerCase().trim();
    if (!ans) continue;
    if (ans.includes('confidential')) return 'Confidential';
    if (ans.includes('private')) return 'Private';
    if (ans.includes('internal use') || ans === 'internal' || ans.includes('internal use')) return 'Internal Use';
    if (ans.includes('public')) return 'Public';
  }
  return 'Public';
};

export default function BoostPolicyFlow({ baseRoute = '/revisr/boost-policy', isOnboarding = false }: BoostPolicyFlowProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isLoading: userIsLoading } = useUser();

  const isOnboardingMode = isOnboarding || sessionStorage.getItem('boost_onboarding_mode') === 'true';

  useEffect(() => {
    if (isOnboarding) {
      sessionStorage.setItem('boost_onboarding_mode', 'true');
      sessionStorage.setItem('boost_policy_base_route', baseRoute);
    }
  }, []);
  const { toast } = useToast();

  const [activeOrgId, setActiveOrgId] = useState<string | null>(getActiveWorkspaceId);

  useEffect(() => {
    const handleWorkspaceChanged = () => {
      setActiveOrgId(getActiveWorkspaceId());
    };
    window.addEventListener('workspace-changed', handleWorkspaceChanged);
    return () => window.removeEventListener('workspace-changed', handleWorkspaceChanged);
  }, []);

  const initialStepIndex = () => {
    const stepParam = searchParams.get("step") as BoostPolicyStepName | null;
    if (stepParam && STEP_NAME_TO_INDEX[stepParam] !== undefined) {
      return STEP_NAME_TO_INDEX[stepParam];
    }
    return 0;
  };

  const [currentStepIndex, setCurrentStepIndex] = useState<number>(initialStepIndex);
  const [policyRecordId, setPolicyRecordId] = useState<string | null>(
    searchParams.get("id"),
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isResuming, setIsResuming] = useState<boolean>(!!searchParams.get("id"));
  const [formData, setFormData] = useState<BoostPolicyFormData>(() => {
    const savedFormData = sessionStorage.getItem("boost_policy_form_data");
    if (savedFormData && searchParams.get("step")) {
      try {
        return { ...INITIAL_FORM_DATA, ...(JSON.parse(savedFormData) as Partial<BoostPolicyFormData>) };
      } catch {
        return INITIAL_FORM_DATA;
      }
    }
    return INITIAL_FORM_DATA;
  });

  const [knowledgeDocuments, setKnowledgeDocuments] = useState<BoostPolicyKnowledgeDocument[]>([]);
  const [isKnowledgeLoading, setIsKnowledgeLoading] = useState<boolean>(false);
  const [contextCategories, setContextCategories] = useState<ContextCategory[]>([]);
  const [standardAnswers, setStandardAnswers] = useState<StandardAnswers>({});
  const [brandingData, setBrandingData] = useState<BoostPolicyBrandingData | null>(null);
  const [isBrandingLoading, setIsBrandingLoading] = useState<boolean>(false);
  const [isOrgContextLoading, setIsOrgContextLoading] = useState<boolean>(false);
  const [orgProfile, setOrgProfile] = useState<OrganizationProfileData | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [showBoostConfirm, setShowBoostConfirm] = useState<boolean>(false);
  const [policyLimit, setPolicyLimit] = useState<{ current?: number; limit?: number } | null>(null);

  const { canCreatePolicy, policyUsage, isLoading: usageLoading, refreshUsage } = useSubscriptionUsage();

  useEffect(() => {
    if (!usageLoading && !canCreatePolicy && policyUsage && !policyRecordId) {
      setPolicyLimit({ current: policyUsage.current, limit: policyUsage.limit });
    }
  }, [usageLoading, canCreatePolicy, policyUsage, policyRecordId]);

  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [isSkillsLoading, setIsSkillsLoading] = useState<boolean>(false);
  const [qaMemoryContent, setQaMemoryContent] = useState<string>('');
  const [qaSessionPairs, setQaSessionPairs] = useState<{ question: string; answer: string }[]>([]);
  // Document classification (Public / Internal Use / Private / Confidential)
  // extracted from the Q&A Tier 0 answer; drives the policy footer label.
  const [documentClassification, setDocumentClassification] = useState<string>('Public');

  const currentStep = STEPS[currentStepIndex];

  useEffect(() => {
    if (!userIsLoading && !user) {
      navigate("/login");
    }
  }, [navigate, user, userIsLoading]);

  useEffect(() => {
    const fetchKnowledgeDocuments = async () => {
      if (!user?.id || !activeOrgId) return;

      setIsKnowledgeLoading(true);
      try {
        const documentList = await loadBoostPolicyKnowledgeDocuments();
        setKnowledgeDocuments(documentList);
      } catch (error) {
        console.error("Failed to load knowledge base documents:", error);
        toast({
          title: "Unable to load knowledge base documents",
          description: "Please refresh and try again.",
          variant: "destructive",
        });
      } finally {
        setIsKnowledgeLoading(false);
      }
    };

    fetchKnowledgeDocuments();
  }, [toast, user?.id, activeOrgId]);

  useEffect(() => {
    const fetchBranding = async () => {
      if (!activeOrgId) {
        setBrandingData(null);
        return;
      }

      setIsBrandingLoading(true);
      try {
        const branding = await BrandingService.getOrganizationBranding(activeOrgId);
        setBrandingData((branding as BoostPolicyBrandingData) || null);
      } catch (error) {
        console.error("[BoostPolicy] Failed to load branding:", error);
        setBrandingData(null);
      } finally {
        setIsBrandingLoading(false);
      }
    };

    fetchBranding();
  }, [activeOrgId]);

  // Scroll to top whenever the step changes (fires after render, not before)
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [currentStepIndex]);

  useEffect(() => {
    fetchContextCategories()
      .then(setContextCategories)
      .catch((err) => console.warn("[BoostPolicy] Could not load context categories:", err));
  }, []);

  useEffect(() => {
    const loadSavedOrgContext = async () => {
      if (!activeOrgId) return;

      setIsOrgContextLoading(true);
      try {
        const savedContext = await getOrgContext();
        if (savedContext) {
          const rawContext = savedContext.organizationContext?.trim() || "";
          const usePlaceholder = !rawContext || LOREM_IPSUM_PATTERN.test(rawContext);

          setFormData((prev) => ({
            ...prev,
            organizationContext: usePlaceholder ? "" : rawContext,
          }));

          if (savedContext.profile) {
            setOrgProfile(savedContext.profile);
          }

          if (savedContext.standard_answers) {
            setStandardAnswers(savedContext.standard_answers as StandardAnswers);
          }
        }
      } catch (error) {
        // Not fatal - new users won't have org context yet; the flow works without it
        console.warn("[BoostPolicy] Could not load saved org context (may be a new org):", error);
      } finally {
        setIsOrgContextLoading(false);
      }
    };

    loadSavedOrgContext();
  }, [activeOrgId]);

  useEffect(() => {
    const fetchSkills = async () => {
      setIsSkillsLoading(true);
      try {
        const loaded = await listAiSkills();
        // Only policy_generation skills are selectable in the Boost Policy flow (Step 4).
        // System-category skills are for org context only and must not appear here.
        const policySkills = loaded.filter((s: AiSkill) => s.category === 'policy_generation');
        setSkills(policySkills);

        const savedFormData = sessionStorage.getItem("boost_policy_form_data");
        if (!savedFormData || !searchParams.get("step")) {
          const defaultIds = loaded.filter((s: AiSkill) => s.default_flag).map((s: AiSkill) => s.id);
          if (defaultIds.length > 0) {
            setFormData((prev) => ({ ...prev, selectedSkillIds: defaultIds }));
          }
        }
      } catch (error) {
        console.error("[BoostPolicy] Failed to load AI skills:", error);
      } finally {
        setIsSkillsLoading(false);
      }
    };

    fetchSkills();
  }, []);

  useEffect(() => {
    if (!policyRecordId) {
      setIsResuming(false);
      return;
    }

    const resumeFromDb = async () => {
      setIsResuming(true);
      try {
        const record = await getBoostPolicyById(policyRecordId);
        const auditResults = record.audit_results || {};

        const restoredTemplateId: string | null = auditResults.selectedTemplateId ?? null;

        // Fetch full template metadata so selectedTemplateName and selectedTemplateData
        // are populated even when sessionStorage has been cleared (e.g. after browser close).
        const restoredTemplateData = restoredTemplateId
          ? await getTemplateMetadataById(restoredTemplateId)
          : null;

        setFormData((prev) => ({
          ...prev,
          policyName: record.policy_name === "Untitled" ? "" : record.policy_name,
          selectedKnowledgeDocument: auditResults.selectedKnowledgeDocument || prev.selectedKnowledgeDocument,
          organizationContext: record.generation_prompt || prev.organizationContext,
          industryTags: auditResults.industryTags || prev.industryTags,
          regionTags: auditResults.regionTags || prev.regionTags,
          workforceSize: auditResults.workforceSize || prev.workforceSize,
          selectedSkillIds: auditResults.selectedSkillIds || prev.selectedSkillIds,
          selectedTemplateId: restoredTemplateId ?? prev.selectedTemplateId,
          selectedTemplateName: restoredTemplateData?.name ?? prev.selectedTemplateName,
          selectedTemplateData: restoredTemplateData ?? prev.selectedTemplateData,
          relevantContext: auditResults.relevantContext ?? prev.relevantContext ?? [],
        }));

        // Restore the wizard step from the DB record (source of truth) when the
        // URL lacks a valid ?step= param - e.g. after the browser was closed and
        // re-opened. Without this, the flow always reset to step 1 (library).
        const stepParam = searchParams.get("step") as BoostPolicyStepName | null;
        const urlStepValid = stepParam !== null && STEP_NAME_TO_INDEX[stepParam] !== undefined;
        if (!urlStepValid && typeof record.current_step === "number") {
          const resumeIndex = Math.min(Math.max(record.current_step, 0), STEPS.length - 1);
          setCurrentStepIndex(resumeIndex);
          navigate(`${baseRoute}?step=${STEP_INDEX_TO_NAME[resumeIndex]}&id=${policyRecordId}`, { replace: true });
        }
      } catch (error) {
        console.error("[BoostPolicy] Failed to resume from DB:", error);
        toast({
          title: "Resume failed",
          description: "Could not load saved progress. Starting fresh.",
          variant: "destructive",
        });
        setPolicyRecordId(null);
      } finally {
        setIsResuming(false);
      }
    };

    resumeFromDb();
  }, [policyRecordId]);

  const unlockedCategoryIds = useMemo(() => {
    return contextCategories
      .filter((cat) => isCategoryUnlocked(cat.id, cat.questions, standardAnswers))
      .map((cat) => cat.id);
  }, [contextCategories, standardAnswers]);

  const categoryIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    contextCategories.forEach((cat) => { map[cat.id] = cat.name; });
    return map;
  }, [contextCategories]);

  const isCurrentStepValid = useMemo(() => {
    if (currentStepIndex === 0) return formData.selectedKnowledgeDocument !== null;
    if (currentStepIndex === 1) return formData.policyName.trim().length > 0;
    // Step 2 (context-qa) uses its own Continue button inside the component
    return true;
  }, [currentStepIndex, formData]);

  const displayStepNumber = currentStepIndex + 1;

  const updateField = (field: keyof BoostPolicyFormData, value: string | string[]) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSelectKnowledgeDocument = (document: BoostPolicyKnowledgeDocument) => {
    setFormData((previous) => ({
      ...previous,
      selectedKnowledgeDocument: document,
    }));
  };

  const handleSkillsChange = (selectedIds: string[]) => {
    setFormData((previous) => ({ ...previous, selectedSkillIds: selectedIds }));
  };

  const handleNext = async () => {
    if (!isCurrentStepValid) {
      toast({
        title: "Please complete this step",
        description: "Required details are missing before continuing.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (currentStepIndex === 0) {
        const doc = formData.selectedKnowledgeDocument;
        if (!doc) return;
        const prefillName = formData.policyName.trim() || doc.title;
        if (!policyRecordId) {
          const result = await createBoostPolicyDraft({
            policyStoreId: doc.id,
            selectedKnowledgeDocument: doc,
          });
          setPolicyRecordId(result.id);
          setFormData((prev) => ({ ...prev, policyName: prefillName }));
          sessionStorage.setItem("boost_policy_record_id", result.id);
          refreshUsage().catch(() => {/* non-critical */});
          const nextStep = STEP_INDEX_TO_NAME[1];
          setCurrentStepIndex(1);
          navigate(`${baseRoute}?step=${nextStep}&id=${result.id}`, { replace: true });
        } else {
          await updateBoostPolicyStep(policyRecordId, {
            current_step: 1,
            status: "draft",
            policy_store_id: doc.id,
            template_store_id: doc.id,
            audit_results: { selectedKnowledgeDocument: doc },
          });
          setFormData((prev) => ({ ...prev, policyName: prefillName }));
          const nextStep = STEP_INDEX_TO_NAME[1];
          setCurrentStepIndex(1);
          navigate(`${baseRoute}?step=${nextStep}&id=${policyRecordId}`, { replace: true });
        }
      } else if (currentStepIndex === 1) {
        if (!policyRecordId) {
          toast({ title: "Session error", description: "Policy record not found. Please start over.", variant: "destructive" });
          return;
        }
        await updateBoostPolicyStep(policyRecordId, {
          current_step: 2,
          status: "draft",
          policy_name: formData.policyName,
          branding_data: brandingData,
          audit_results: {
            selectedKnowledgeDocument: formData.selectedKnowledgeDocument,
          },
        });
        const nextStep = STEP_INDEX_TO_NAME[2];
        setCurrentStepIndex(2);
        navigate(`${baseRoute}?step=${nextStep}&id=${policyRecordId}`, { replace: true });
      } else if (currentStepIndex === 2) {
        // context-qa step - navigation is triggered by PolicyContextQAStep's onComplete
        // This branch is here for the Back/forward keyboard nav edge case
        const nextStep = STEP_INDEX_TO_NAME[3];
        setCurrentStepIndex(3);
        if (policyRecordId) {
          navigate(`${baseRoute}?step=${nextStep}&id=${policyRecordId}`, { replace: true });
        }
      }
    } catch (error) {
      console.error("[BoostPolicy] Failed to save step:", error);
      if ((error as Record<string, unknown>).isLimitReached) {
        setPolicyLimit({
          current: (error as Record<string, unknown>).current as number,
          limit: (error as Record<string, unknown>).limit as number,
        });
      } else {
        toast({
          title: "Save failed",
          description: "Could not save your progress. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStepIndex === 0) {
      navigate(isOnboardingMode ? "/onboarding" : "/revisr/dashboard");
      return;
    }

    const prevIndex = Math.max(currentStepIndex - 1, 0);
    setCurrentStepIndex(prevIndex);

    if (policyRecordId) {
      const stepName = STEP_INDEX_TO_NAME[prevIndex];
      navigate(`${baseRoute}?step=${stepName}&id=${policyRecordId}`, { replace: true });
    }
  };

  const handleGenerate = async () => {
    if (!isCurrentStepValid) return;

    if (!formData.selectedKnowledgeDocument?.id) {
      toast({
        title: "No library document selected",
        description: "Please select a library document before generating.",
        variant: "destructive",
      });
      return;
    }

    if (!policyRecordId) {
      toast({ title: "Session error", description: "Policy record not found. Please start over.", variant: "destructive" });
      return;
    }

    setIsGenerating(true);

    try {
      const selectedTemplate = await loadSelectedTemplate(formData.selectedTemplateId);
      const fullAuditResults = {
        selectedKnowledgeDocument: formData.selectedKnowledgeDocument,
        industryTags: formData.industryTags,
        regionTags: formData.regionTags,
        workforceSize: formData.workforceSize,
        selectedSkillIds: formData.selectedSkillIds,
        selectedTemplateId: selectedTemplate?.id ?? null,
        relevantContext: formData.relevantContext ?? [],
      };

      await updateBoostPolicyStep(policyRecordId, {
        current_step: 4,
        status: "draft",
        audit_results: fullAuditResults,
        // Persist the Q&A Tier 0 classification answer into revisr_final_policies.metadata
        // (merged server-side with existing metadata). Only the classification is stored
        // here — the editor loadFn reads it to seed the footer on cross-tab/DB resume.
        metadata: { classification: documentClassification },
      });

      const payload = {
        ...createBoostPolicyBrandPayload(formData, brandingData, selectedTemplate),
        relevantContext: (formData.relevantContext ?? []).length > 0 ? formData.relevantContext : undefined,
        qaSessionPairs: qaSessionPairs.length > 0 ? qaSessionPairs : undefined,
        additionalContext: formData.additionalContext?.trim() || undefined,
        documentClassification,
      };

      sessionStorage.setItem("workflow_type", "brand_policy");
      sessionStorage.setItem("revisr_brand_policy_data", JSON.stringify(payload));
      sessionStorage.setItem("boost_policy_form_data", JSON.stringify(formData));
      sessionStorage.setItem("boost_policy_record_id", policyRecordId);
      sessionStorage.removeItem("revisr_brand_policy_result");

      navigate(`/revisr/brand-policy/generating?id=${policyRecordId}`);
    } catch (error) {
      console.error("Failed to start boost policy generation:", error);
      toast({
        title: "Generation failed",
        description: "Unable to start policy generation. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (userIsLoading || isResuming) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // — Policy limit wall ———————————————————————————
  if (policyLimit) {
    return (
      <RevisrLayout>
        <div className="container mx-auto px-6 py-8 bg-background">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground">Boost your Policy</h1>
            <p className="text-muted-foreground mt-2">
              Use the Revisr library to generate organisation-ready policies tailored to your context.
            </p>
          </div>
          <Card className="border-2 border-orange-200 shadow-sm">
            <CardContent className="py-12 px-8 flex flex-col items-center text-center">
              <div className="h-16 w-16 rounded-full bg-orange-100 flex items-center justify-center mb-5">
                <Lock className="h-8 w-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Policy Limit Reached</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-sm">
                You've used all the policies allowed on your current plan. Upgrade to create unlimited policies.
              </p>

              {policyLimit.limit !== undefined && (
                <div className="w-full max-w-xs bg-white border border-orange-100 rounded-lg p-4 mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Policies used</span>
                    <span className="font-semibold text-gray-800">
                      {policyLimit.current ?? '-'} / {policyLimit.limit}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-400 rounded-full"
                      style={{
                        width: `${Math.min(
                          ((policyLimit.current ?? 0) / policyLimit.limit) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              <Button variant="outline" onClick={() => navigate('/revisr/dashboard')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </RevisrLayout>
    );
  }

  // — Step 0: Full-page policy library card grid ———————————————
  if (currentStepIndex === 0) {
    return (
      <RevisrLayout>
        <div className="flex-1 overflow-y-auto py-6">
          <PolicyLibraryGrid
            documents={knowledgeDocuments}
            isLoading={isKnowledgeLoading}
            selectedDocId={formData.selectedKnowledgeDocument?.id ?? null}
            onSelect={(doc) => setFormData((prev) => ({ ...prev, selectedKnowledgeDocument: doc }))}
            onContinue={handleNext}
            isSaving={isSaving}
            unlockedCategoryIds={unlockedCategoryIds}
            categoryIdToName={categoryIdToName}
          />
        </div>
      </RevisrLayout>
    );
  }

  // — Steps 1â€“4: Wizard inside card —————————————————————
  return (
    <RevisrLayout>
      <PolicyStepBar
        steps={['Choose Document', 'Policy Setup', 'AI Context', 'Review & Boost']}
        current={displayStepNumber}
      />
      <div className="container mx-auto px-4 py-5 md:px-6 md:py-8 bg-background">
        <div className="mb-5 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Boost your Policy</h1>
          <p className="text-muted-foreground mt-1 md:mt-2 text-sm">
            Multi-step flow to tailor and generate organisation-ready policies.
          </p>
        </div>

        <Card className="border border-border shadow-sm">
          <CardHeader>
            <p className="text-xs text-muted-foreground font-medium mb-1">Step {displayStepNumber} of 4</p>
            <CardTitle>{currentStep.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{currentStep.description}</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentStep.id === "policy-library" && (
              <LibrarySelectionStep
                documents={knowledgeDocuments}
                selectedDocument={formData.selectedKnowledgeDocument}
                isLoading={isKnowledgeLoading}
                onSelect={handleSelectKnowledgeDocument}
              />
            )}

            {currentStep.id === "policy-setup" && (
              <PolicySetupStep
                data={formData}
                onChange={updateField}
                orgId={activeOrgId || ""}
                brandingData={brandingData}
                orgProfile={orgProfile}
                profileLoading={isOrgContextLoading}
                brandingLoading={isBrandingLoading}
              />
            )}

            {currentStep.id === "context-qa" && (
              <PolicyContextQAStep
                orgId={activeOrgId || ""}
                orgProfile={orgProfile}
                documentTitle={formData.selectedKnowledgeDocument?.title || ""}
                policyName={formData.policyName}
                relevantContext={formData.relevantContext ?? []}
                documentTags={formData.selectedKnowledgeDocument?.tags ?? []}
                onComplete={(memory, pairs, additionalCtx) => {
                  setQaMemoryContent(memory);
                  setQaSessionPairs(pairs);
                  setDocumentClassification(extractClassification(pairs));
                  if (additionalCtx.trim()) {
                    setFormData((prev) => ({ ...prev, additionalContext: additionalCtx.trim() }));
                  }
                  const nextStep = STEP_INDEX_TO_NAME[3];
                  setCurrentStepIndex(3);
                  if (policyRecordId) {
                    navigate(`${baseRoute}?step=${nextStep}&id=${policyRecordId}`, { replace: true });
                    // Persist the review step so a browser-close here resumes to review,
                    // not back to QA. Fire-and-forget - non-critical to the QA flow.
                    updateBoostPolicyStep(policyRecordId, { current_step: 3, status: "draft" }).catch(
                      (err) => console.warn("[BoostPolicy] Could not persist review step:", err),
                    );
                  }

                }}
              />
            )}

            {currentStep.id === "review" && (
              <GeneratePolicyStep
                data={formData}
                brandingData={brandingData}
                orgProfile={orgProfile}
                skills={skills}
                isSkillsLoading={isSkillsLoading}
                onSkillsChange={handleSkillsChange}
                qaMemoryContent={qaMemoryContent}
                qaSessionPairs={qaSessionPairs}
              />
            )}

            <div className="flex items-center gap-3 pt-5 mt-2 border-t border-border" data-testid="step-nav">
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                disabled={isGenerating || isSaving}
                className="gap-2 min-w-[90px] sm:min-w-[100px]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              {/* context-qa step manages its own Continue button - hide the global one */}
              {currentStep.id !== "context-qa" && (
                currentStepIndex < STEPS.length - 1 ? (
                  <Button
                    size="lg"
                    onClick={handleNext}
                    disabled={!isCurrentStepValid || isGenerating || isSaving}
                    className="flex-1 gap-2 bg-[#33b864] hover:bg-[#2da358] text-white"
                    data-testid="step-next"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={() => setShowBoostConfirm(true)}
                    disabled={!isCurrentStepValid || isGenerating || isSaving}
                    className="flex-1 gap-2 bg-[#33b864] hover:bg-[#2da358] text-white"
                    data-testid="step-next"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Starting generation...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Ready to boost
                      </>
                    )}
                  </Button>
                )
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={showBoostConfirm} onOpenChange={setShowBoostConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ready to generate your policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will use your selected document, template, and organisation context to generate the policy with AI. You can review the output before saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isGenerating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isGenerating}
              className="bg-[#33b864] hover:bg-[#2da358] text-white"
              onClick={handleGenerate}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Starting...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Policy
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RevisrLayout>
  );
}


